import type { paths } from './generated/v1';

// The shared default envelope is emitted on every route. Preserve unfamiliar
// server codes so an older client can still expose a newer API failure.
type PublicError =
  paths['/account-connections']['get']['responses'][400]['content']['application/json'];
type ErrorEnvelope = Omit<PublicError, 'code' | 'issues'> & {
  code: string;
  issues?: PublicError['issues'];
  stack?: string;
};

type ApiResponse<T> = {
  response: Response;
  data?: T;
  error?: unknown;
};

export class ApiClientError extends Error {
  code: ErrorEnvelope['code'];
  issues: PublicError['issues'];
  context: PublicError['context'];
  reason: PublicError['reason'];
  links: PublicError['links'];
  retryable: PublicError['retryable'];

  constructor(public error: ErrorEnvelope) {
    super(error.message);
    this.code = error.code;
    this.issues = error.issues ?? [];
    this.context = error.context;
    this.reason = error.reason;
    this.links = error.links;
    this.retryable = error.retryable;
    if (error.stack) this.stack = error.stack;
  }
}

export class ApiClientResponseError extends Error {
  constructor(
    message: string,
    public status: number,
    public readonly details: {
      contentType: string | null;
      requestId: string | null;
      cfRay: string | null;
    } = { contentType: null, requestId: null, cfRay: null }
  ) {
    super(message);
    this.name = 'ApiClientResponseError';
  }
}

export function throwIfError<
  T extends ApiResponse<unknown> | Promise<ApiResponse<unknown>>,
>(
  response: T
): T extends Promise<ApiResponse<unknown>>
  ? Promise<NonNullable<Awaited<T>['data']>>
  : NonNullable<Awaited<T>['data']>;
export function throwIfError(
  response: Promise<ApiResponse<unknown>> | ApiResponse<unknown>
): unknown {
  if (response instanceof Promise)
    return response.then((value) => throwIfError(value));
  if (response.response.ok && response.error === undefined) {
    return response.data;
  }

  const error = response.error;
  if (
    typeof error !== 'object' ||
    error === null ||
    !('code' in error) ||
    typeof error.code !== 'string' ||
    !('message' in error) ||
    typeof error.message !== 'string' ||
    !('issues' in error) ||
    !Array.isArray(error.issues) ||
    !error.issues.every(
      (issue: unknown) =>
        typeof issue === 'object' &&
        issue !== null &&
        'message' in issue &&
        typeof issue.message === 'string' &&
        (!('path' in issue) ||
          issue.path === undefined ||
          (Array.isArray(issue.path) &&
            issue.path.every(
              (part: unknown) =>
                typeof part === 'string' || typeof part === 'number'
            ))) &&
        (!('schema' in issue) ||
          issue.schema === undefined ||
          typeof issue.schema === 'string')
    ) ||
    ('retryable' in error &&
      error.retryable !== undefined &&
      typeof error.retryable !== 'boolean') ||
    ('links' in error &&
      error.links !== undefined &&
      (typeof error.links !== 'object' ||
        error.links === null ||
        !('docs' in error.links) ||
        typeof error.links.docs !== 'string' ||
        !('schema' in error.links) ||
        typeof error.links.schema !== 'string'))
  )
    throw new ApiClientResponseError(
      'API response did not contain a valid error envelope',
      response.response.status,
      {
        contentType: response.response.headers.get('content-type'),
        requestId: response.response.headers.get('x-request-id'),
        cfRay: response.response.headers.get('cf-ray'),
      }
    );

  throw new ApiClientError(error as ErrorEnvelope);
}
